import { Button } from "@/components/ui/button";
import TableHeader from "./TableHeader";
import { useLoginUser } from "@/hooks/useAuth";
import CustomInput from "./CustomInput";
import { DateUtils } from "@/utils/dayUtill";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/toast/CustomToast";
import formatPhoneNumber from "../util/formatPhone";
import { cn } from "@/utils/cn";
import ErrorMsg from "./errorMsg";
import CustomModal from "@/components/modal/CustomModal";
import { useEffect, useState } from "react";
import PasswordChangeBtn from "./passwordChangeBtn";
import type { PersonnelBasicInfo } from "@/hooks/usePersonnelBasicInfo";
import httpClient from "@/config/axios";
import { CircleAlert } from "lucide-react";
import useThrottle from "@/hooks/useThrottle";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

type PickNurseDetailData = Pick<
  PersonnelBasicInfo,
  "joining_date" | "experience" | "phone_number" | "email"
>;

// 연락처 스키마 (인증번호 포함)
const phoneFormSchema = z.object({
  new_phone_number: z
    .string()
    .min(1, "연락처를 입력해주세요")
    .regex(/^010-\d{4}-\d{4}$/, "올바른 연락처 형식이 아닙니다"),
  verification_code: z
    .string()
    .min(1, "인증번호를 입력해주세요")
    .length(6, "인증번호는 6자리입니다"),
});

// 이메일 + 경력 스키마
const basicInfoFormSchema = z.object({
  email: z
    .string()
    .min(1, "이메일을 입력해주세요")
    .email("올바른 이메일 형식이 아닙니다"),
  experience: z.number().min(0, "경력은 0년 이상이어야 합니다"),
});

const NO_PHONE_MESSAGE = "등록된 연락처가 없습니다";

/** API·DB placeholder(예: "--")·빈 값·포맷 불가 시 안내 문구 */
function displayPhoneForMyPage(raw: string | null | undefined): string {
  const s = (raw ?? "").trim();
  if (!s || s === "--") return NO_PHONE_MESSAGE;
  const formatted = formatPhoneNumber(s);
  return formatted.trim() !== "" ? formatted : NO_PHONE_MESSAGE;
}

/** 입사일 없음·"--"·Invalid Date 시 재직기간도 "-" */
function displayTenureForMyPage(
  joiningDate: string | Date | null | undefined
): string {
  if (joiningDate == null) return "-";
  if (joiningDate instanceof Date) {
    if (Number.isNaN(joiningDate.getTime())) return "-";
    return (
      DateUtils.getTenureString(joiningDate, new Date().toISOString()) || "-"
    );
  }
  const s = String(joiningDate).trim();
  if (!s || s === "--") return "-";
  if (!DateUtils.parseKoreanDate(s).isValid()) return "-";
  return DateUtils.getTenureString(s, new Date().toISOString()) || "-";
}

function myPageReadonlyValueClass(isEmpty: boolean): string {
  return cn(
    "py-3 px-4 font-pretendard font-normal text-[18px]",
    isEmpty ? "text-muted-foreground/50" : "text-text-sub"
  );
}

function myPageReadonlySpanClass(isEmpty: boolean): string {
  return cn(
    "font-pretendard font-normal text-[18px]",
    isEmpty ? "text-muted-foreground/50" : "text-text-sub"
  );
}

type UserFormProps = PickNurseDetailData & {
  /** true면 이메일·총 경력 수정 UI 숨김 (계정관리 등) */
  isAdmin?: boolean;
};

export default function UserForm({
  isAdmin = false,
  joining_date,
  experience,
  phone_number = "",
  email = "",
}: UserFormProps) {
  const queryClient = useQueryClient();
  const loginUser = useLoginUser();
  const throttle = useThrottle(1500); // 1.5초 쓰로틀링
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isBasicInfoModalOpen, setIsBasicInfoModalOpen] = useState(false);

  // 편집 모드 상태
  const [isEmailEditing, setIsEmailEditing] = useState(false);
  const [isExperienceEditing, setIsExperienceEditing] = useState(false);

  // 인증번호 발송 관련 상태
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [countdown, setCountdown] = useState<number>(0); // 3분 = 180초

  // 인증번호 부터 발송
  const phoneFormMethods = useForm<z.infer<typeof phoneFormSchema>>({
    resolver: zodResolver(phoneFormSchema),
    defaultValues: {
      new_phone_number: "",
    },
  });

  // 이메일 + 경력 통합 Form
  const basicInfoFormMethods = useForm<z.infer<typeof basicInfoFormSchema>>({
    resolver: zodResolver(basicInfoFormSchema),
    defaultValues: {
      email: email ?? "",
      experience: experience ?? 0,
    },
  });

  // Props 변경 시 Form defaultValues 갱신 (연락처는 모달에서 새로 입력하므로 제외)

  useEffect(() => {
    basicInfoFormMethods.reset({
      email: email ?? "",
      experience: experience ?? 0,
    });
  }, [email, experience, basicInfoFormMethods]);

  // 카운트다운 타이머
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && isVerificationSent) {
      // 카운트다운이 0이 되면 초기화
      setCountdown(0);
    }
  }, [countdown, isVerificationSent]);

  // 카운트다운 포맷팅 (MM:SS)
  const formatCountdown = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // 하이픈 제거 유틸리티
  const removeHyphens = (phone: string) => phone.replace(/-/g, "");

  // 인증번호 발송 Mutation
  const { mutate: sendVerificationCode } = useMutation({
    mutationFn: async (phoneNumber: string) => {
      console.log(removeHyphens(phoneNumber));

      await httpClient.post("/nurses/change-phone/send-code", {
        new_phone_number: removeHyphens(phoneNumber),
      });
    },
    onSuccess: () => {
      setIsVerificationSent(true);
      setCountdown(180); // 3분 = 180초
      toast.success("인증번호가 발송되었습니다.");
    },

    onSettled: () => {
      setIsSendingVerification(false);
    },
  });

  // 연락처 수정 Mutation
  const { mutate: mutatePhone, isPending: isPhonePending } = useMutation({
    mutationFn: async (data: z.infer<typeof phoneFormSchema>) => {
      await httpClient.put("/nurses/change-phone/verify", {
        new_phone_number: removeHyphens(data.new_phone_number),
        verification_code: data.verification_code,
      });
    },
    onSuccess: () => {
      phoneFormMethods.reset({
        new_phone_number: "",
        verification_code: "",
      });
      setIsPhoneModalOpen(false);
      setIsVerificationSent(false);
      setCountdown(0);

      // 캐싱 무효화 및 즉시 refetch
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.nurses.personnelBasicInfo(),
      });
      queryClient.invalidateQueries({ queryKey: ["my-page"] });
      // 즉시 refetch하여 화면 갱신
      queryClient.refetchQueries({
        queryKey: QUERY_KEYS.nurses.personnelBasicInfo(),
      });
      toast.success("연락처가 저장되었습니다.");
    },
  });

  // 이메일 + 경력 수정 Mutation
  const { mutate: mutateBasicInfo, isPending: isBasicInfoPending } =
    useMutation({
      mutationFn: async (data: z.infer<typeof basicInfoFormSchema>) => {
        await httpClient.patch("/nurses/personnel-basic-info", data, {
          withCredentials: true,
        });
      },
      onSuccess: (_, variables) => {
        setIsBasicInfoModalOpen(false);
        setIsEmailEditing(false);
        setIsExperienceEditing(false);

        const currentEmail = basicInfoFormMethods.getValues("email");
        const currentExperience = basicInfoFormMethods.getValues("experience");

        // 성공 후 기준값(defaultValues) 업데이트
        basicInfoFormMethods.reset({
          email: variables.email ?? currentEmail,
          experience: variables.experience ?? currentExperience,
        });

        queryClient.refetchQueries({ queryKey: ["my-page"] });
        toast.success("정보가 수정 되었습니다.");
      },
    });

  // --- 연락처 핸들러 (별도 동작) ---
  const handlePhoneEdit = () => {
    // 모달 열 때 빈 값으로 초기화
    phoneFormMethods.reset({
      new_phone_number: "",
      verification_code: "",
    });
    setIsVerificationSent(false);
    setIsPhoneModalOpen(true);
  };

  const handlePhoneCancel = () => {
    phoneFormMethods.reset({
      new_phone_number: "",
      verification_code: "",
    });
    setIsPhoneModalOpen(false);
    setIsVerificationSent(false);
    setCountdown(0);
  };

  const handleSendVerification = async () => {
    throttle(async () => {
      const phoneNumber = phoneFormMethods.getValues("new_phone_number");
      const isValid = await phoneFormMethods.trigger("new_phone_number");
      if (isValid) {
        setIsSendingVerification(true);
        // 재발송인 경우 타이머 초기화
        if (isVerificationSent) {
          setCountdown(180);
        }
        sendVerificationCode(phoneNumber);
      }
    });
  };

  const onPhoneSubmit = (data: z.infer<typeof phoneFormSchema>) => {
    throttle(() => {
      mutatePhone(data);
    });
  };

  // --- 이메일 핸들러 (경력과 상호 배타적) ---
  const handleEmailEdit = () => {
    // 경력 수정 중이면 끄고 초기화
    if (isExperienceEditing) {
      setIsExperienceEditing(false);
      basicInfoFormMethods.resetField("experience");
    }
    // 이메일 수정 시작
    setIsEmailEditing(true);
  };

  const handleEmailPreSubmit = async () => {
    const isValid = await basicInfoFormMethods.trigger("email");
    if (isValid) setIsBasicInfoModalOpen(true);
  };

  const handleEmailCancel = () => {
    basicInfoFormMethods.resetField("email");
    setIsEmailEditing(false);
  };

  // --- 경력 핸들러 (이메일과 상호 배타적) ---
  const handleExperienceEdit = () => {
    // 이메일 수정 중이면 끄고 초기화
    if (isEmailEditing) {
      setIsEmailEditing(false);
      basicInfoFormMethods.resetField("email");
    }
    // 경력 수정 시작
    setIsExperienceEditing(true);
  };

  const handleExperiencePreSubmit = async () => {
    const isValid = await basicInfoFormMethods.trigger("experience");
    if (isValid) setIsBasicInfoModalOpen(true);
  };

  const handleExperienceCancel = () => {
    basicInfoFormMethods.resetField("experience");
    setIsExperienceEditing(false);
  };

  // --- 통합 Submit 핸들러 ---
  const onBasicInfoSubmit = (data: z.infer<typeof basicInfoFormSchema>) => {
    const dirtyFields = basicInfoFormMethods.formState.dirtyFields;
    const payload: Partial<z.infer<typeof basicInfoFormSchema>> = {};

    if (dirtyFields.email) {
      payload.email = data.email;
    }
    if (dirtyFields.experience) {
      payload.experience = data.experience;
    }

    if (Object.keys(payload).length === 0) return;

    mutateBasicInfo(payload as z.infer<typeof basicInfoFormSchema>);
  };

  /**
   * 확인 모달에서 `handleSubmit`을 쓰면 스키마 전체(이메일 포함)가 검증되어,
   * 경력만 저장할 때도 이메일이 비어 있으면 오류가 난다.
   * dirty 필드만 trigger 후 제출한다.
   */
  const confirmBasicInfoSubmit = async () => {
    const dirtyFields = basicInfoFormMethods.formState.dirtyFields;
    const fieldsToValidate: ("email" | "experience")[] = [];
    if (dirtyFields.email) fieldsToValidate.push("email");
    if (dirtyFields.experience) fieldsToValidate.push("experience");

    if (fieldsToValidate.length === 0) {
      setIsBasicInfoModalOpen(false);
      return;
    }

    const isValid = await basicInfoFormMethods.trigger(fieldsToValidate);
    if (!isValid) return;

    onBasicInfoSubmit(basicInfoFormMethods.getValues());
  };

  // 에러 메시지 배열
  const phoneErrorMsg = Object.values(phoneFormMethods.formState.errors).map(
    (error) => error.message
  );

  const empSeq = loginUser?.EmpSeqNo;
  const isEmpSeqEmpty =
    empSeq == null || (typeof empSeq === "string" && empSeq.trim() === "");

  const accountId = loginUser?.account_id;
  const isAccountIdEmpty =
    accountId == null ||
    (typeof accountId === "string" && accountId.trim() === "");

  const phoneDisplayText = displayPhoneForMyPage(phone_number);
  const isPhoneDisplayEmpty = phoneDisplayText === NO_PHONE_MESSAGE;

  const emailDisplayTrimmed = (email ?? "").trim();
  const isEmailDisplayEmpty = emailDisplayTrimmed === "";

  const joiningValid =
    !!joining_date && DateUtils.parseKoreanDate(joining_date).isValid();
  const joiningDateDisplay = joiningValid
    ? DateUtils.dateFormatKR(joining_date, "YYYY년 MM월 DD일")
    : "-";

  const tenureDisplayText = displayTenureForMyPage(joining_date);
  const isTenureEmpty = tenureDisplayText === "-";

  return (
    <>
      <CustomModal.Confirm
        isOpen={isPhoneModalOpen}
        onClose={handlePhoneCancel}
        onConfirm={phoneFormMethods.handleSubmit(onPhoneSubmit)}
        isPending={isPhonePending}
        pendingMsg="연락처를 저장하는 중입니다..."
        cancelText="닫기"
        confirmText="변경"
      >
        <FormProvider {...phoneFormMethods}>
          <div className="px-0 ">
            <div>
              <h3 className="text-[16px] text-center text-sig font-semibold font-pretendard mb-10">
                연락처 변경
              </h3>
            </div>
            {phoneErrorMsg.length > 0 && (
              <div className="text-destructive text-sm mt-2 mb-10 flex items-center gap-3  text-center justify-center">
                <CircleAlert />
                <span>{phoneErrorMsg[0]}</span>
              </div>
            )}

            <div className="flex flex-col gap-4 w-full">
              <div className="grid grid-cols-[130px_2fr] gap-2 items-center">
                <span className="text-base font-pretendard font-medium">
                  휴대폰번호
                </span>
                <div className="flex gap-2">
                  <CustomInput
                    type="text"
                    placeholder="연락처를 입력해주세요"
                    {...phoneFormMethods.register("new_phone_number")}
                    disabled={isVerificationSent}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      phoneFormMethods.setValue("new_phone_number", formatted, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      // 인증번호 발송 후 번호 변경 시 초기화
                      if (isVerificationSent) {
                        setIsVerificationSent(false);
                        phoneFormMethods.setValue("verification_code", "");
                      }
                    }}
                    className={cn(
                      "placeholder:text-gray-400 text-sm!",
                      phoneFormMethods.formState.errors.new_phone_number &&
                        "border-red-300 shadow-lg shadow-red-300/20"
                    )}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSendVerification}
                    disabled={
                      isSendingVerification ||
                      (!isVerificationSent &&
                        (!phoneFormMethods.watch("new_phone_number") ||
                          !!phoneFormMethods.formState.errors.new_phone_number))
                    }
                    className="whitespace-nowrap px-4 py-2 rounded-full border-sig text-sig hover:bg-sig/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSendingVerification
                      ? "발송중..."
                      : isVerificationSent
                        ? "재발송"
                        : "인증번호 발송"}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-[130px_2fr] gap-2 items-center">
                <span className="text-base font-pretendard font-medium">
                  인증번호
                </span>
                <div className="flex items-center gap-2">
                  <div className="relative w-full">
                    <CustomInput
                      type="text"
                      placeholder="인증번호 6자리"
                      {...phoneFormMethods.register("verification_code")}
                      disabled={!isVerificationSent}
                      maxLength={6}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        phoneFormMethods.setValue("verification_code", value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        });
                      }}
                      className={cn(
                        "placeholder:text-gray-400 text-sm!",
                        phoneFormMethods.formState.errors.verification_code &&
                          "border-red-300 shadow-lg shadow-red-300/20",
                        isVerificationSent &&
                          !phoneFormMethods.formState.errors
                            .verification_code &&
                          phoneFormMethods.watch("verification_code")
                            ?.length === 6 &&
                          "border-green-200 bg-green-100"
                      )}
                    />
                    <div className="absolute right-5 top-1/2 -translate-y-1/2">
                      {isVerificationSent && countdown > 0 && (
                        <span className="text-sm text-gray-500 font-pretendard whitespace-nowrap">
                          {formatCountdown(countdown)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* info */}
            <p className="text-sm text-gray-400 text-center my-10 max-w-[390px] mx-auto break-words leading-relaxed">
              * 휴대폰번호를 입력한 후 인증번호를 발송해주세요.
              <br />* 인증번호는 6자리 숫자입니다.
            </p>
          </div>
        </FormProvider>
      </CustomModal.Confirm>

      {!isAdmin && (
        <CustomModal.Confirm
          isOpen={isBasicInfoModalOpen}
          onClose={() => setIsBasicInfoModalOpen(false)}
          onConfirm={confirmBasicInfoSubmit}
          isPending={isBasicInfoPending}
          title="정보를 수정하시겠습니까?"
          pendingMsg="정보를 저장하는 중입니다..."
        />
      )}

      <div className="">
        <table className="w-full table-fixed">
          <tbody className="divide-y divide-gray-100 text-sm">
            <tr>
              <TableHeader className="font-pretendard text-[16px] ">
                사번
              </TableHeader>
              <td className={myPageReadonlyValueClass(isEmpSeqEmpty)}>
                {loginUser?.EmpSeqNo ?? "-"}
              </td>
            </tr>
            <tr>
              <TableHeader className="font-pretendard text-[16px] ">
                아이디
              </TableHeader>
              <td className={myPageReadonlyValueClass(isAccountIdEmpty)}>
                {loginUser?.account_id ?? "-"}
              </td>
            </tr>
            <tr>
              <TableHeader className="font-semibold font-pretendard text-[16px] ">
                비밀번호
              </TableHeader>
              <td className="py-3 px-4">
                <PasswordChangeBtn />
              </td>
            </tr>

            {/* 연락처 */}
            <tr>
              <TableHeader className="font-pretendard text-[16px] ">
                연락처
              </TableHeader>
              <td className="py-3 px-4 flex gap-4 items-center justify-between">
                <span
                  className={cn(
                    myPageReadonlySpanClass(isPhoneDisplayEmpty),
                    "text-base"
                  )}
                >
                  {phoneDisplayText}
                </span>
                <Button
                  variant="outline"
                  type="button"
                  className="shadow-none py-[10px] px-[14px] border-sig rounded-full bg-transparent flex justify-center items-center"
                  onClick={handlePhoneEdit}
                >
                  <span className="text-sig text-[15px]! font-semibold !font-pretendard ">
                    수정
                  </span>
                </Button>
              </td>
            </tr>

            {/* 이메일 + 경력 Form */}
            <FormProvider {...basicInfoFormMethods}>
              {/* 이메일 Row */}
              <tr>
                <TableHeader className="font-pretendard text-[16px] text-text-main2">
                  이메일
                </TableHeader>
                <td
                  className={
                    isAdmin
                      ? myPageReadonlyValueClass(isEmailDisplayEmpty)
                      : "py-3 px-4 flex gap-4 items-center justify-between"
                  }
                >
                  {isAdmin ? (
                    isEmailDisplayEmpty ? "-" : emailDisplayTrimmed
                  ) : (
                    <>
                      <div className="flex flex-col w-full!">
                        <CustomInput
                          {...basicInfoFormMethods.register("email")}
                          autoComplete="off"
                          disabled={!isEmailEditing}
                          placeholder="이메일을 입력해주세요 ex) example@email.com"
                          className={cn(
                            basicInfoFormMethods.formState.errors.email &&
                              "border-red-400"
                          )}
                        />
                        {basicInfoFormMethods.formState.errors.email && (
                          <ErrorMsg
                            message={
                              basicInfoFormMethods.formState.errors.email
                                .message ?? ""
                            }
                            className="ml-3 font-pretendard"
                          />
                        )}
                      </div>

                      {!isEmailEditing ? (
                        <Button
                          variant="outline"
                          type="button"
                          className="shadow-none py-[10px] px-[14px] border-sig rounded-full bg-transparent flex justify-center items-center"
                          onClick={handleEmailEdit}
                        >
                          <span className="text-sig text-[15px]! font-semibold !font-pretendard ">
                            수정
                          </span>
                        </Button>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            type="button"
                            className={`shadow-none py-[10px] px-[14px] border-gray-300 rounded-full bg-transparent flex justify-center items-center ${basicInfoFormMethods.formState.errors.email && "self-start"}`}
                            onClick={handleEmailCancel}
                          >
                            <span className="text-gray-600 text-[15px]! font-semibold !font-pretendard ">
                              취소
                            </span>
                          </Button>
                          <Button
                            type="button"
                            disabled={
                              !basicInfoFormMethods.formState.dirtyFields.email
                            }
                            className={`rounded-full py-[10px] px-[15px] bg-sig hover:bg-sig/80 disabled:opacity-50 disabled:cursor-not-allowed ${basicInfoFormMethods.formState.errors.email && "self-start"}`}
                            onClick={handleEmailPreSubmit}
                          >
                            <span className="text-white text-[15px]! font-semibold !font-pretendard">
                              저장
                            </span>
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </td>
              </tr>

              <tr>
                <TableHeader className="font-pretendard text-[16px] text-text-main2">
                  입사일
                </TableHeader>
                <td
                  className={cn(
                    myPageReadonlyValueClass(!joiningValid),
                    "w-[210px]"
                  )}
                >
                  {joiningDateDisplay}
                </td>
              </tr>
              <tr>
                <TableHeader className="font-pretendard text-[16px] text-text-main2">
                  재직기간
                </TableHeader>
                <td className={myPageReadonlyValueClass(isTenureEmpty)}>
                  {tenureDisplayText}
                </td>
              </tr>

              {/* 경력 Row */}
              <tr>
                <TableHeader className="font-pretendard text-[16px] text-text-main2">
                  총 경력
                </TableHeader>
                <td
                  className={
                    isAdmin
                      ? myPageReadonlyValueClass(false)
                      : "py-3 px-4 text-gray-800 flex gap-4 items-center "
                  }
                >
                  {isAdmin ? (
                    <span className="font-pretendard text-[18px] text-text-sub">
                      {experience ?? 0}년
                    </span>
                  ) : (
                    <>
                      <div className="flex items-center">
                        <CustomInput
                          {...basicInfoFormMethods.register("experience", {
                            setValueAs: (v) =>
                              v === "" || v === null ? 0 : Number(v),
                          })}
                          type="number"
                          min={0}
                          disabled={!isExperienceEditing}
                          value={
                            basicInfoFormMethods.watch("experience") ?? 0
                          }
                          onChange={(e) => {
                            const val = e.target.value
                              .replace(/[^0-9]/g, "")
                              .replace(/^0+(?!$)/, "");

                            basicInfoFormMethods.setValue(
                              "experience",
                              val === "" ? 0 : Number(val),
                              {
                                shouldValidate: true,
                                shouldDirty: true,
                              }
                            );
                          }}
                          placeholder="경력을 입력하세요"
                          className="w-24"
                        />
                        <span className="ml-2 text-text-main2 font-pretendard text-[18px]">
                          년
                        </span>
                      </div>

                      {!isExperienceEditing ? (
                        <Button
                          variant="outline"
                          type="button"
                          className="shadow-none py-[10px] px-[14px] border-sig rounded-full bg-transparent flex justify-center items-center ml-[13px]"
                          onClick={handleExperienceEdit}
                        >
                          <span className="text-sig text-[15px]! font-semibold !font-pretendard ">
                            수정
                          </span>
                        </Button>
                      ) : (
                        <div className="flex gap-2 ml-[13px]">
                          <Button
                            variant="outline"
                            type="button"
                            className="shadow-none py-[10px] px-[14px] border-gray-300 rounded-full bg-transparent flex justify-center items-center"
                            onClick={handleExperienceCancel}
                          >
                            <span className="text-gray-600 text-[15px]! font-semibold !font-pretendard ">
                              취소
                            </span>
                          </Button>
                          <Button
                            type="button"
                            disabled={
                              !basicInfoFormMethods.formState.dirtyFields
                                .experience
                            }
                            className="rounded-full py-[10px] px-[15px] bg-sig hover:bg-sig/80 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleExperiencePreSubmit}
                          >
                            <span className="text-white text-[15px]! font-semibold !font-pretendard">
                              저장
                            </span>
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </td>
              </tr>
            </FormProvider>
          </tbody>
        </table>
      </div>
    </>
  );
}
